import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { removeBreadcrumb } from 'redux/actions/layoutActions';

import { L10n, createElement, setCulture } from '@syncfusion/ej2-base';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Page,
  Inject,
  Filter,
  Resize,
} from '@syncfusion/ej2-react-grids';
import useApi from 'hooks/useApi';
import { TextBox } from '@syncfusion/ej2-react-inputs';

import { TextField } from 'shared/components/form/Inputs';
import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';
import { colors } from 'shared/defaultTheme';
import { setContactsCount } from 'redux/actions/resultActions';
import { capitalizeFirstLetter } from 'shared/utils';
import PropTypes from 'prop-types';
import { ClientResultsWrapper } from '../styles';
import { L10nObj } from '../contants';

const ContactResults = forwardRef(({ getCounters }, ref) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { breadcrumbs } = useSelector((state) => state.layout);
  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState();
  const { cusNavId } = useSelector((state) => state.auth);
  const { customAreaApi } = useApi();
  const [searchValue, setSearchValue] = useState('');
  const { generalSearchText } = useSelector((state) => state.results);
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const [language, setLanguage] = useState('es');
  const [pageSettings, setPageSettings] = useState({});
  const [pageSize, setPageSize] = useState(10);
  const grid = useRef(null);

  let data;
  let dropInstance;

  useEffect(() => {
    const lang = selectedDefaultLanguage.slice(0, 2);
    setLanguage(lang);
    setCulture(lang);
    L10n.load(L10nObj);
  }, [selectedDefaultLanguage]);

  const [, getContacts] = customAreaApi(
    {
      url: `${window.API_V1}/cli/contacts/${cusNavId}/search/all`,
      method: 'POST',
    },
    { manual: true }
  );

  const [, getContactsCount] = customAreaApi(
    {
      url: `${window.API_V1}/cli/contacts/${cusNavId}/search/all/count`,
      method: 'POST',
    },
    { manual: true }
  );

  const setLoader = (value) => {
    if (!document.querySelector('.e-spinner-pane')) return;
    if (value) {
      document.querySelector('.e-spinner-pane').style.display = 'block';
    } else {
      document.querySelector('.e-spinner-pane').style.display = 'none';
    }
  };

  const getData = async (state) => {
    setLoader(true);
    let filtering;
    let sortingFilter = sorting;

    if (state?.action?.action === 'filter' || state?.action?.action === 'clearFilter') {
      filtering = state?.where?.[0].predicates || [];
      setFilters(filtering);
    }

    const currentFilters = filtering || filters;
    const dataFilters = currentFilters.map((x) => {
      const filter = {
        model: 'Contact',
        field: x.field.charAt(0).toUpperCase() + x.field.slice(1),
        filter: [x.value],
        IsRegexDiacritics: true,
      };
      return filter;
    });

    if (state?.action?.requestType === 'sorting' && state?.action?.columnName) {
      sortingFilter = {
        model: 'Contact',
        field: capitalizeFirstLetter(state?.action?.columnName),
        asc: state?.action?.direction === 'Ascending',
      };
    }

    if (state?.action?.requestType === 'sorting' && !state?.action?.columnName) {
      sortingFilter = null;
    }

    setSorting(sortingFilter);

    dataFilters.push(
      sortingFilter || {
        model: 'Contact',
        field: 'Created',
        asc: false,
      }
    );

    dataFilters.push({
      model: 'Contact',
      field: 'Admin',
      filter: [false],
    });

    const body = {
      filters: dataFilters,
      reduce: {
        limit: state.take,
        skip: state.skip / pageSize + 1,
      },
      ...((searchValue || generalSearchText) && {
        filterText: {
          regexFilter: searchValue || generalSearchText,
        },
      }),
    };

    try {
      const {
        data: { data: count },
      } = await getContactsCount({
        data: body,
      });

      const {
        data: { data: contacts },
      } = await getContacts({
        data: body,
      });

      dispatch(setContactsCount(count));
      setLoader(false);
      return {
        count,
        result: contacts,
      };
    } catch (error) {
      console.error(`Error Contacts: ${error}`);
      return {
        count: 0,
        result: [],
      };
    }
  };

  const dataStateChange = async (state) => {
    setPageSize(state.take);
    const requestType = state?.action?.requestType;
    if (requestType === 'stringfilterrequest') return;
    const dataSource = await getData(state);
    if (grid.current) {
      grid.current.dataSource = dataSource;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const state = { skip: 0, take: pageSize };
      await dataStateChange(state);
    };
    setPageSettings({ pageCount: 3, pageSize, pageSizes: [5, 10, 20, 50, 75, 100] });
    fetchData();
  }, []);

  const getFilter = (field) => {
    const filter = filters.find((x) => x.field === field);
    return filter?.value || '';
  };

  const customFilter = {
    ui: {
      create: (args) => {
        document.getElementsByClassName('e-flm_optrdiv')[0].style.display = 'none';
        document.getElementsByClassName('e-flmenu-valuediv')[0].style.padding = '0';
        const flValInput = createElement('input', { className: 'flm-input' });
        args.target.appendChild(flValInput);
        dropInstance = new TextBox({
          type: 'text',
          placeholder: t('results_filter_search_value'),
          value: getFilter(args.column.field),
        });
        dropInstance.appendTo(flValInput);
      },
      read: (args) => {
        grid.current.removeFilteredColsByField(args.column.field);
        args.fltrObj.filterByColumn(args.column.field, 'contains', dropInstance.value);
      },
      write: (args) => {
        if (args.filteredValue) {
          dropInstance.value = args.filteredValue;
        }
      },
    },
  };

  useImperativeHandle(ref, () => ({
    async doSearch() {
      const state = { skip: 0, take: pageSize };
      await getCounters();
      await dataStateChange(state);
    },
  }));

  const handleSearch = (e) => {
    if (e) {
      const { value } = e.target;
      setSearchValue(value);
    } else {
      setSearchValue('');
    }
  };

  const handleRowSelected = (row) => {
    const { data: currentContact } = row;
    if (breadcrumbs.length > 1) {
      breadcrumbs.forEach(() => {
        dispatch(removeBreadcrumb());
      });
    }
    history.push(`/clients/update/${currentContact.clientId}`);
  };

  const onSearch = async () => {
    const state = { skip: 0, take: pageSize };
    await dataStateChange(state);
  };

  const onInputChange = async (e) => {
    if (e.key === 'Enter') {
      await onSearch();
    }
  };

  useEffect(() => {
    (async () => {
      if (searchValue === '') {
        const state = { skip: 0, take: pageSize };
        await dataStateChange(state);
      }
    })();
  }, [searchValue]);

  return (
    <ClientResultsWrapper>
      <div className="filter-container">
        <div className="form flex-column" noValidate>
          <div className="d-flex flex-row align-items-center h-50 ml-filter-section selector-filter-by-wrapper" />
          <TextField
            borderRadius={2}
            className="mb-4 m-0 h-50 filter-input"
            inputClassName="w-100"
            withButtonClear
            rightIcon={
              <SearchIcon fill={colors.black} onClick={onSearch} style={{ cursor: 'pointer' }} />
            }
            placeholder={t('results_search_placeholder_contacts')}
            value={searchValue ?? ''}
            onChange={handleSearch}
            onKeyDown={onInputChange}
          />
        </div>
      </div>

      <div className="control-pane">
        <div className="control-section">
          <GridComponent
            id="Grid"
            dataSource={data}
            ref={(g) => {
              grid.current = g;
            }}
            allowPaging
            pageSettings={pageSettings}
            dataStateChange={dataStateChange}
            allowSorting
            allowFiltering
            filterSettings={{
              type: 'Menu',
              ignoreAccent: true,
            }}
            locale={language}
            rowSelected={(args) => {
              handleRowSelected(args);
            }}
          >
            <ColumnsDirective>
              <ColumnDirective
                field="name"
                headerTemplate={t('clients_name_column')}
                width="120"
                textAlign="Left"
                filter={customFilter}
              />
              <ColumnDirective
                field="surname"
                headerText={t('contacts_surname_column')}
                width="120"
                textAlign="Left"
                filter={customFilter}
              />
              <ColumnDirective
                field="mail"
                headerText={t('contacts_email_column')}
                width="200"
                textAlign="Left"
                filter={customFilter}
              />
              <ColumnDirective
                field="phone"
                headerText={t('contacts_mobile_column')}
                width="120"
                textAlign="Left"
                filter={customFilter}
              />
              <ColumnDirective
                field="description"
                headerText={t('contacts_description_column')}
                width="120"
                textAlign="Left"
                filter={customFilter}
              />
              <ColumnDirective
                field="clientName"
                headerText={t('general_client')}
                width="150"
                textAlign="Left"
                filter={customFilter}
              />
            </ColumnsDirective>
            <Inject services={[Page, Filter, Resize]} />
          </GridComponent>
        </div>
        <div id="waitingpopup" className="waitingpopup">
          <span id="gif" className="image" />
        </div>
      </div>
    </ClientResultsWrapper>
  );
});

ContactResults.propTypes = {
  getCounters: PropTypes.func.isRequired,
};

export default ContactResults;
